import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FormField from "@/components/ui/formField";
import { useAuth } from "@/hooks/auth/useAuth";
import useLocalStorage from "@/hooks/store/useLocalStorage";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { LoginFormProps, LoginPayload as LoginFormData } from "@/types/auth";

import { twMerge } from "tailwind-merge";

import { Button } from "../ui/button";
import Checkbox from "../ui/checkbox";
import { LOGIN_RULES } from "./data";

export const LoginForm = ({
  setPageType,
  isLoggingIn = false,
  onSubmit
}: LoginFormProps): JSX.Element => {
  const { isLoginWithSsoEnabled } = useAppFeatures();
  const { getSsoProvider, isGettingSsoProvider } = useAuth();

  // TODO: make abstraction layer when we have many provider later
  const [getItem, setItem, removeItem] = useLocalStorage("rememberMe", {
    email: ""
  });
  const [step, setStep] = useState(1);
  const [rememberMe, setRememberMe] = useState(!!getItem?.email);

  const formLogin = useForm<LoginFormData>({
    mode: "onChange",
    defaultValues: { email: getItem?.email || "", password: "" }
  });
  const {
    register,
    formState,
    resetField,
    watch,
    reset,
    trigger,
    handleSubmit
  } = formLogin;

  const submitLogin = (e: React.FormEvent<HTMLFormElement>) => {
    if (rememberMe) {
      setItem({ email: watch("email") });
    } else {
      removeItem();
    }
    handleSubmit((payload: LoginFormData) => {
      onSubmit?.(payload);
    })(e);
  };

  const goToEnterPasswordStep = async () => {
    const isValid = await trigger("email"); // Manually validate the "email" field
    if (isValid) {
      setStep(2);
    }
  };

  const handleContinueClick = async () => {
    const email = watch("email");
    if (!isLoginWithSsoEnabled) {
      return goToEnterPasswordStep();
    }

    try {
      const domain = email?.split("@")[1];
      await getSsoProvider(domain);
    } catch {
      goToEnterPasswordStep();
    }
  };

  return (
    <FormProvider {...formLogin}>
      <form className="mt-8" onSubmit={submitLogin}>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-1">
            <h2 className="text-3xl font-semibold text-txtHeading">
              Welcome Back
            </h2>
            {!watch("email") && (
              <p className="text-sm text-txtHeading">
                Enter your email to sign in.
              </p>
            )}
            {watch("email") && step > 1 && (
              <p className="text-sm text-txtHeading">
                {watch("email")}.
                <button
                  type="button"
                  className="text-primary-teal text-sm underline hover:cursor-pointer ml-1"
                  onClick={() => {
                    resetField("password");
                    setStep(1);
                  }}
                >
                  edit
                </button>
              </p>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <FormField
              data-testid="login-email-input"
              placeHolder="Email address"
              register={register}
              name="email"
              rules={LOGIN_RULES.email}
              error={formState.errors.email?.message}
              resetValue={() => {
                resetField("email", { defaultValue: "" });
              }}
              className={step === 1 ? "" : "hidden"}
              autoCleanError={false}
              hasSpacingOnFocus={false}
              disabled={isLoggingIn}
            />
            <FormField
              data-testid="login-password-input"
              placeHolder="Password"
              register={register}
              name="password"
              type="password"
              className={step === 1 ? "hidden" : ""}
              autoCleanError={false}
              hasSpacingOnFocus={false}
              disabled={isLoggingIn}
            />

            <div
              data-test-id="login-options-section"
              className="flex justify-between"
            >
              <span className="flex justify-between items-center gap-1">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={setRememberMe}
                />
                <label
                  htmlFor="rememberMe"
                  className="text-txtBody text-sm font-normal"
                >
                  Remember me
                </label>
              </span>
              <button
                type="button"
                className="text-txtBody text-sm font-semibold underline hover:cursor-pointer"
                onClick={() => {
                  reset();
                  setStep(1);
                  setPageType("resetPassword");
                }}
              >
                Forgot password?
              </button>
            </div>
          </div>
          <Button
            type="button"
            disabled={
              isGettingSsoProvider || !watch("email") || !formState.isValid
            }
            className={twMerge(
              "rounded-xl font-semibold text-base py-3",
              (!watch("email") || !formState.isValid) &&
                "bg-gray-300 disabled:opacity-70",
              step > 1 && "hidden"
            )}
            onClick={handleContinueClick}
          >
            Continue
          </Button>
          <Button
            type="submit"
            disabled={!watch("password") || isLoggingIn}
            className={twMerge(
              "rounded-xl font-semibold text-base py-3",
              !watch("password") && "bg-gray-300 disabled:opacity-70",
              step === 1 && "hidden"
            )}
          >
            Sign In
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default LoginForm;
